import React, { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';

/**
 * QuickApplicationModal - Modal for quickly adding a new application
 * @param {Boolean} isOpen - Whether the modal is open
 * @param {Function} onClose - Callback to close the modal
 * @param {Function} onSubmit - Callback to submit the form
 * @param {Object} initialStage - Initial stage to assign (optional)
 * @param {Array} recruiters - List of available recruiters
 * @param {Boolean} isLoading - Loading state
 */
const QuickApplicationModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  initialStage = null,
  recruiters = [],
  isLoading = false
}) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    desiredPosition: '',
    experienceYears: '',
    assignedTo: '',
    coverLetter: '',
    internalNotes: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      // Reset form when opening
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        desiredPosition: '',
        experienceYears: '',
        assignedTo: '',
        coverLetter: '',
        internalNotes: ''
      });
      setErrors({});
    }
  }, [isOpen]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const data = {
      ...formData,
      experienceYears: formData.experienceYears ? parseFloat(formData.experienceYears) : null
    };

    onSubmit(data);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Quick Add Application
            </h2>
            {initialStage && (
              <p className="text-sm text-gray-500 mt-1">
                Will be added to: <span className="font-medium text-purple-600">{initialStage.label}</span>
              </p>
            )}
          </div>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Name Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className={`form-input ${errors.firstName ? 'border-red-500 focus:ring-red-500' : ''}`}
                placeholder="John"
                autoFocus
              />
              {errors.firstName && (
                <p className="text-xs text-red-500 mt-1">{errors.firstName}</p>
              )}
            </div>
            <div>
              <label className="form-label">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className={`form-input ${errors.lastName ? 'border-red-500 focus:ring-red-500' : ''}`}
                placeholder="Doe"
              />
              {errors.lastName && (
                <p className="text-xs text-red-500 mt-1">{errors.lastName}</p>
              )}
            </div>
          </div>

          {/* Contact Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`form-input ${errors.email ? 'border-red-500 focus:ring-red-500' : ''}`}
                placeholder="john.doe@example.com"
              />
              {errors.email && (
                <p className="text-xs text-red-500 mt-1">{errors.email}</p>
              )}
            </div>
            <div>
              <label className="form-label">Phone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="form-input"
                placeholder="+1 (555) 123-4567"
              />
            </div>
          </div>

          {/* Position & Experience */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">Desired Position</label>
              <input
                type="text"
                name="desiredPosition"
                value={formData.desiredPosition}
                onChange={handleChange}
                className="form-input"
                placeholder="e.g., Math Teacher"
              />
            </div>
            <div>
              <label className="form-label">Experience (Years)</label>
              <input
                type="number"
                name="experienceYears"
                value={formData.experienceYears}
                onChange={handleChange}
                min="0"
                max="50"
                step="0.5"
                className="form-input"
                placeholder="5"
              />
            </div>
          </div>

          {/* Assigned Recruiter */}
          {recruiters.length > 0 && (
            <div>
              <label className="form-label">Assign To</label>
              <select
                name="assignedTo"
                value={formData.assignedTo}
                onChange={handleChange}
                className="form-input"
              >
                <option value="">-- Unassigned --</option>
                {recruiters.map(recruiter => (
                  <option key={recruiter.id} value={recruiter.id}>
                    {recruiter.first_name} {recruiter.last_name} ({recruiter.email})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Cover Letter */}
          <div>
            <label className="form-label">Cover Letter / Notes</label>
            <textarea
              name="coverLetter"
              value={formData.coverLetter}
              onChange={handleChange}
              className="form-input"
              rows={3}
              placeholder="Brief cover letter or initial notes..."
            />
          </div>

          {/* Internal Notes */}
          <div>
            <label className="form-label">Internal Notes (Private)</label>
            <textarea
              name="internalNotes"
              value={formData.internalNotes}
              onChange={handleChange}
              className="form-input"
              rows={2}
              placeholder="Internal notes not visible to applicant..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary flex items-center space-x-2"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <span>Add Application</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuickApplicationModal;
